/**
 * A schedule as a person describes it, and the cron expression it compiles to.
 *
 * The server's vocabulary is a five-field cron expression and stays that way —
 * it is what the timer parses, what the Source Profile stores, and what an
 * operator copying a line out of a crontab would expect. What changed is who
 * has to write one. The people configuring this portal are not the people who
 * remember that the fifth field is day-of-week and that Sunday is both 0 and 7,
 * so the screen asks four ordinary questions and assembles the expression here.
 *
 * The translation goes both ways, and the reverse direction is the one that
 * needs care. `fromCron` returns `null` for any expression outside the four
 * shapes below — `*\/15 * * * *`, `0 6 1 * 1`, anything with a list or a range.
 * Null is not an error: it means this schedule is real and firing and simply
 * cannot be drawn as four dropdowns, so the panel shows the expression as it
 * stands rather than rounding it to the nearest thing the picker can say.
 * Silently rewriting somebody's schedule to fit the form would be the worst
 * outcome available here.
 */

/** How often a Run repeats. The four shapes the picker can say. */
export type Recurrence = "hourly" | "daily" | "weekly" | "monthly";

export interface SchedulePlan {
  recurrence: Recurrence;
  /** Minutes past the hour, 0–59. */
  minute: number;
  /** Hour of the day in UTC, 0–23. Ignored when `recurrence` is hourly. */
  hour: number;
  /** 0 is Sunday, matching cron. Only read when `recurrence` is weekly. */
  weekday: number;
  /** Day of the month, 1–31. Only read when `recurrence` is monthly. */
  monthDay: number;
}

/** Monday at six, the shape almost every source wants. */
export const DEFAULT_PLAN: SchedulePlan = {
  recurrence: "weekly",
  minute: 0,
  hour: 6,
  weekday: 1,
  monthDay: 1,
};

export const RECURRENCE_LABELS: Record<Recurrence, string> = {
  hourly: "Every hour",
  daily: "Every day",
  weekly: "Every week",
  monthly: "Every month",
};

export const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export function toCron(plan: SchedulePlan): string {
  const { minute, hour, weekday, monthDay } = plan;
  switch (plan.recurrence) {
    case "hourly":
      return `${minute} * * * *`;
    case "daily":
      return `${minute} ${hour} * * *`;
    case "weekly":
      return `${minute} ${hour} * * ${weekday}`;
    case "monthly":
      return `${minute} ${hour} ${monthDay} * *`;
  }
}

/**
 * The plan an expression describes, or null if it describes something richer.
 *
 * Only the exact output of `toCron` round-trips, with one concession: cron
 * writes Sunday as 0 or 7 interchangeably, and an expression saved elsewhere
 * may well use 7.
 */
export function fromCron(expression: string | null | undefined): SchedulePlan | null {
  const parts = (expression ?? "").trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minuteText, hourText, dayText, monthText, weekdayText] = parts;

  const minute = wholeNumber(minuteText, 0, 59);
  if (minute === null || monthText !== "*") return null;

  // Every hour: the hour field is open and neither day field is restricted.
  if (hourText === "*") {
    if (dayText !== "*" || weekdayText !== "*") return null;
    return { ...DEFAULT_PLAN, recurrence: "hourly", minute };
  }

  const hour = wholeNumber(hourText, 0, 23);
  if (hour === null) return null;

  if (dayText === "*" && weekdayText === "*") {
    return { ...DEFAULT_PLAN, recurrence: "daily", minute, hour };
  }

  if (dayText === "*") {
    const weekday = wholeNumber(weekdayText, 0, 7);
    if (weekday === null) return null;
    return { ...DEFAULT_PLAN, recurrence: "weekly", minute, hour, weekday: weekday === 7 ? 0 : weekday };
  }

  if (weekdayText === "*") {
    const monthDay = wholeNumber(dayText, 1, 31);
    if (monthDay === null) return null;
    return { ...DEFAULT_PLAN, recurrence: "monthly", minute, hour, monthDay };
  }

  // Both day fields restricted. Cron matches on *either* of them, which is the
  // one rule nobody predicts, so the picker declines to draw it.
  return null;
}

/** The sentence under the controls: what was chosen, said back in full. */
export function describe(plan: SchedulePlan): string {
  const time = `${pad(plan.hour)}:${pad(plan.minute)} UTC`;
  switch (plan.recurrence) {
    case "hourly":
      return plan.minute === 0
        ? "Runs every hour, on the hour."
        : `Runs every hour, at ${plan.minute} minutes past.`;
    case "daily":
      return `Runs every day at ${time}.`;
    case "weekly":
      return `Runs every ${WEEKDAYS[plan.weekday] ?? "day"} at ${time}.`;
    case "monthly":
      return `Runs on the ${ordinal(plan.monthDay)} of every month at ${time}.`;
  }
}

/**
 * The chosen UTC time said again in the reader's own clock, or null when they
 * are the same.
 *
 * Schedules are stored and fired in UTC and that is not negotiable here — but
 * "06:00 UTC" is not an answer to "will this wake me up", which is the question
 * somebody in another timezone is actually asking. Today's offset is used, so
 * the hint moves by an hour across a daylight-saving boundary while the Run
 * itself does not. That is the honest way round: the schedule is the fixed
 * thing and the local reading of it is what drifts.
 */
export function localTimeHint(hour: number, minute: number): string | null {
  const now = new Date();
  const moment = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hour, minute),
  );
  if (moment.getHours() === hour && moment.getMinutes() === minute) return null;
  const local = moment.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const zone =
    new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(moment)
      .find((part) => part.type === "timeZoneName")?.value ?? "local time";
  return `${local}${dayShift(moment)} ${zone}`;
}

/**
 * Whether the local reading of a moment lands on a different calendar day.
 *
 * Compared as whole days rather than by day-of-month: 23:45 UTC on the 31st is
 * the 1st locally in any eastward zone, and `1 > 31` says the day before.
 */
function dayShift(moment: Date): string {
  const utcDay = Date.UTC(moment.getUTCFullYear(), moment.getUTCMonth(), moment.getUTCDate());
  const localDay = Date.UTC(moment.getFullYear(), moment.getMonth(), moment.getDate());
  const days = Math.round((localDay - utcDay) / 86_400_000);
  if (days === 0) return "";
  return days > 0 ? " the next day" : " the day before";
}

/** `06:00`, the value an `<input type="time">` reads and writes. */
export function toTimeValue(plan: SchedulePlan): string {
  return `${pad(plan.hour)}:${pad(plan.minute)}`;
}

/** The hour and minute of a `<input type="time">`, or null if it is empty. */
export function fromTimeValue(value: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

/** A field that is exactly one number in range, or null for anything else. */
function wholeNumber(text: string | undefined, low: number, high: number): number | null {
  if (!text || !/^\d+$/.test(text)) return null;
  const value = Number(text);
  return value >= low && value <= high ? value : null;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export function ordinal(value: number): string {
  const suffix =
    value % 100 >= 11 && value % 100 <= 13
      ? "th"
      : { 1: "st", 2: "nd", 3: "rd" }[value % 10] ?? "th";
  return `${value}${suffix}`;
}
